<div class="content-info">
  <div class="content-title">
    <h3 class="title">Stock Status</h3>
    <div class="title-actions">
      <!-- Search by Product Name and SKU -->
      <label class="zf-search" title="Search by product name or SKU">
        <i class="bi bi-search"></i>
        <input type="text" [(ngModel)]="searchName" (input)="onSearch()" placeholder="Search product or SKU" />
      </label>

      <!-- Location Filter -->
      <div class="form-group mb-0">
        <!-- Roots and their sub-areas (damage area, …) in one list, indented. -->
        <select class="form-select" [(ngModel)]="selectedLocationId" (change)="onSearch()" style="min-width: 220px">
          <option [ngValue]="null" disabled>Select location</option>
          @for (loc of locationOptions; track loc.id) {
          <option [ngValue]="loc.id">
            {{ loc.depth ? '   ↳ ' + loc.name : loc.name }}
          </option>
          }
        </select>
      </div>

      <!-- Excel export of the current stock snapshot -->
      <button type="button" class="btn btn-secondary text-nowrap" (click)="exportToExcel()"
        [disabled]="exporting || !selectedLocationId">
        <i class="bi bi-download"></i>{{ exporting ? 'Exporting…' : 'Export Excel' }}
      </button>
    </div>
  </div>

  <!-- Where the selected warehouse's stock sits: the sellable row, in
       transit to it, and each sub-area (damage area). Click a sub-area to
       open it. Only rendered when the location has sub-areas. -->
  @if (summary) {
  <div class="zf-tiles mt-2">
    <div class="zf-tile">
      <span class="zf-tile-label">On hand</span>
      <span class="zf-tile-value">{{ summary.onHandQty | number:'1.0-3' }}</span>
      <span class="zf-tile-meta">Sellable units at this location</span>
    </div>
    @if (summaryIsWarehouse) {
    <!-- Warehouse: what it has dispatched that the stores have not received. -->
    <div class="zf-tile" title="Dispatched by Transfer Out and not yet received at the store">
      <span class="zf-tile-label">In transit to stores</span>
      <span class="zf-tile-value">{{ summary.inTransitOutboundQty | number:'1.0-3' }}</span>
      <span class="zf-tile-meta">{{ summary.inTransitOutboundDocs }} transfer{{ summary.inTransitOutboundDocs === 1 ? '' : 's' }} out</span>
    </div>
    } @else {
    <!-- Store: what the warehouse has sent that this store has not received. -->
    <div class="zf-tile" title="Sent by the warehouse and not yet received here">
      <span class="zf-tile-label">In transit from warehouse</span>
      <span class="zf-tile-value">{{ summary.inTransitInboundQty | number:'1.0-3' }}</span>
      <span class="zf-tile-meta">Awaiting receipt here</span>
    </div>
    }
    @for (area of summary.subAreas; track area.id) {
    <button type="button" class="zf-tile text-start" style="cursor: pointer; font: inherit; color: inherit"
      [style.border-color]="area.type === 'DamageArea' && area.qty > 0 ? 'var(--zf-danger-border)' : null"
      (click)="selectLocation(area.id)" [title]="'Show stock in ' + area.name">
      <span class="zf-tile-label">
        @if (area.type === 'DamageArea') { <i class="bi bi-exclamation-triangle text-danger me-1"></i> }
        {{ area.name }}
      </span>
      <span class="zf-tile-value" [class.text-danger]="area.type === 'DamageArea' && area.qty > 0">{{ area.qty | number:'1.0-3' }}</span>
      <span class="zf-tile-meta">{{ area.skuCount }} SKU{{ area.skuCount === 1 ? '' : 's' }} · click to open</span>
    </button>
    }
  </div>
  }

  <div class="common-table" [class.mt-2]="!summary">
    <div class="table-responsive">
      <table class="table">
        <thead>
          <tr>
            <th style="width: 56px">#</th>
            <th>SKU</th>
            <th>Name</th>
            <th>Location</th>
            <th class="zf-num">Quantity</th>
            <th>Batch</th>
            <th>Expiry</th>
          </tr>
        </thead>
        <tbody>
          @if (!inventoryList.length) {
          <tr>
            <td colspan="7">
              <div class="zf-empty">
                <strong>No inventory records found</strong>
                <span>Pick a location or change the search to see stock on hand.</span>
              </div>
            </td>
          </tr>
          }

          @for (item of inventoryList; track item; let i = $index) {
          <tr>
            <td class="zf-mono zf-dim" style="color: var(--zf-muted-2) !important; font-weight: 400">{{ i + 1 + (currentPage - 1) * pageSize }}</td>
            <td class="zf-key">{{ getProductSKU(item) || "-" }}</td>
            <td>{{ getProductName(item) || "-" }}</td>
            <td class="zf-dim">{{ getLocationName(item) || "-" }}</td>
            <td class="zf-num zf-strong">
              {{
              (item?.quantity ?? item?.variants?.[0]?.inventory?.[0]?.quantity) != null
              ? ((item?.quantity ?? item?.variants?.[0]?.inventory?.[0]?.quantity) | number : "1.0-3")
              : '-'
              }}
            </td>
            <td class="zf-mono zf-dim">
              {{ item?.batchNumber || item?.variants?.[0]?.inventory?.[0]?.batchNumber || '-' }}
            </td>
            <td class="zf-mono zf-dim">
              {{ formatExpiryDate(item?.expiryDate || item?.variants?.[0]?.inventory?.[0]?.expiryDate || '-') }}
            </td>
          </tr>
          }
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="table-pagination">
      <div class="pagination-count">
        <select [(ngModel)]="pageSize" (change)="pageSizeChange()">
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="30">30</option>
          <option value="40">40</option>
        </select>
        <span>entries per page</span>
      </div>
      <ul class="pagination">
        <li class="page-item" [class.disabled]="currentPage === 1" (click)="goToPage(currentPage - 1)">
          <a class="page-link">&lsaquo;</a>
        </li>
        @for (p of visiblePages; track p) {
        <li class="page-item" [class.active]="p === currentPage" (click)="goToPage(p)">
          <a class="page-link">{{ p }}</a>
        </li>
        }
        <li class="page-item" [class.disabled]="currentPage === pageCount" (click)="goToPage(currentPage + 1)">
          <a class="page-link">&rsaquo;</a>
        </li>
      </ul>
    </div>
  </div>
</div>
